import type { CrowdexUIMessagePart } from '@crowdex/agent-contracts';
import { codeStep, isCodeCallPart, isCodeRunPart } from './code-mode';
import type { DynamicToolPart, ToolActivityKind } from './types';

/**
 * Collapse a tool's name and title into a stable, comparison-friendly identity:
 * lowercased with every run of non-alphanumerics folded to a single `_`.
 */
export function normalizeToolId(toolName: string, title: string | undefined): string {
  return `${toolName} ${title ?? ''}`.toLowerCase().replace(/[^a-z0-9]+/g, '_');
}

export function isQuestionToolPart(part: CrowdexUIMessagePart): boolean {
  if (part.type !== 'dynamic-tool') return false;
  const identity = normalizeToolId(part.toolName, part.title);
  if (/(?:^|_)(?:ask_?user_?question|request_?user_?input|ask_?question)(?:_|$)/.test(identity)) {
    return true;
  }
  if (typeof part.input !== 'object' || part.input === null) return false;
  const questions = (part.input as Record<string, unknown>)['questions'];
  return (
    Array.isArray(questions) &&
    questions.length > 0 &&
    questions.every(
      (question) =>
        typeof question === 'object' &&
        question !== null &&
        (typeof (question as Record<string, unknown>)['question'] === 'string' ||
          Array.isArray((question as Record<string, unknown>)['options'])),
    )
  );
}

export function questionItems(part: DynamicToolPart): readonly Record<string, unknown>[] {
  if (typeof part.input !== 'object' || part.input === null) return [];
  const input = part.input as Record<string, unknown>;
  const questions = Array.isArray(input['questions']) ? input['questions'] : [input];
  return questions.filter(
    (value): value is Record<string, unknown> => typeof value === 'object' && value !== null,
  );
}

export function browserToolTitle(
  toolName: string,
  providedTitle: string | undefined,
  input: unknown,
): string | null {
  const identity = `${toolName} ${providedTitle ?? ''}`.toLowerCase();
  const values =
    typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : {};
  const text = (key: string) => (typeof values[key] === 'string' ? values[key] : null);

  if (!identity.includes('browser')) return null;
  if (identity.includes('browser_navigate')) {
    const url = text('url');
    return url === null ? 'Naviguer dans le navigateur' : `Naviguer vers ${url}`;
  }
  if (identity.includes('browser_ax_state')) return 'Lire la page actuelle';
  if (identity.includes('browser_ax_action')) return 'Agir dans la page';
  if (identity.includes('browser_screenshot')) return 'Prendre une capture de la page';
  if (identity.includes('browser_logs')) return 'Lire la console du navigateur';
  if (identity.includes('browser_tabs')) return 'Lister les onglets du navigateur';
  if (identity.includes('browser_reload')) return 'Recharger la page';
  // The `js` tool's own label, which the agent writes for each call (as in Codex).
  const title = text('title');
  if (title !== null && title.trim() !== '') return title.trim();
  return 'Contrôler le navigateur';
}

export function dynamicToolActivityKind(part: DynamicToolPart): ToolActivityKind {
  // Code mode's steps do what their code calls, whatever `run` or `browser` in their names say.
  if (isCodeRunPart(part) || isCodeCallPart(part)) return 'tools';
  const identity = normalizeToolId(part.toolName, part.title);

  if (/(?:edit|write|patch|apply_patch|delete|remove|move|rename|create_file)/.test(identity)) {
    return 'file-changes';
  }
  if (/(?:web|browser|fetch|http|navigate)/.test(identity)) return 'web';
  if (/(?:read|view_file|list_file|glob|grep|search_file|find_file)/.test(identity)) {
    return 'exploration';
  }
  if (/(?:exec|command|shell|bash|terminal|run)/.test(identity)) return 'commands';
  return 'tools';
}

export function dynamicToolAction(part: DynamicToolPart): string {
  const identity = normalizeToolId(part.toolName, part.title);
  const active = part.state === 'input-streaming' || part.state === 'input-available';
  const failed = part.state === 'output-error' || part.state === 'output-denied';

  if (/(?:grep)/.test(identity)) return failed ? 'Grep' : active ? 'Grepping' : 'Grepped';
  if (/(?:glob|search_file|find_file)/.test(identity)) {
    return failed ? 'Search files' : active ? 'Searching files' : 'Searched files';
  }
  if (/(?:read|view_file|list_file|ls)/.test(identity)) {
    return failed ? 'Read' : active ? 'Reading' : 'Read';
  }
  if (/(?:edit|write|patch|apply_patch|create_file)/.test(identity)) {
    return failed ? 'Edit' : active ? 'Editing' : 'Edited';
  }
  if (/(?:delete|remove)/.test(identity)) {
    return failed ? 'Delete' : active ? 'Deleting' : 'Deleted';
  }
  if (/(?:web_search|search_web)/.test(identity)) {
    return failed ? 'Search web' : active ? 'Searching web' : 'Searched web';
  }
  if (/(?:fetch|http)/.test(identity)) return failed ? 'Fetch' : active ? 'Fetching' : 'Fetched';
  if (/(?:browser|navigate)/.test(identity)) return failed ? 'Run' : active ? 'Running' : 'Ran';
  if (/(?:exec|command|shell|terminal|run)/.test(identity)) {
    return failed ? 'Run' : active ? 'Running' : 'Ran';
  }
  return failed ? 'Run' : active ? 'Running' : 'Ran';
}

/** A code-mode step's name: what its code calls ("List avatars"), or null for another tool. */
export function codeToolTitle(part: DynamicToolPart): string | null {
  return isCodeRunPart(part) || isCodeCallPart(part) ? codeStep(part, []).title : null;
}

export function toolDetail(part: DynamicToolPart, readableTitle: string | null): string {
  const code = codeToolTitle(part);
  if (code !== null) return code;
  const values =
    typeof part.input === 'object' && part.input !== null
      ? (part.input as Record<string, unknown>)
      : {};
  const title = (readableTitle ?? part.title ?? '').trim();
  const strippedTitle = title.replace(
    /^(?:reading|read|searching files|searched files|search files|grepping|grepped|grep|editing|edited|edit|writing|wrote|write|deleting|deleted|delete|running|ran|run|fetching|fetched|fetch|searching web|searched web|search web)\s+/i,
    '',
  );
  if (strippedTitle.length > 0 && strippedTitle.toLowerCase() !== part.toolName.toLowerCase()) {
    return strippedTitle;
  }
  for (const key of ['path', 'file_path', 'command', 'url']) {
    const value = values[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return part.toolName.replace(/[_-]+/g, ' ');
}
